import { inject, observer } from "mobx-react";
import { Preloader } from "randmarcomps";
import { User } from "oidc-client-ts";
import React, { useEffect, useState } from "react";
import { RootStore } from "../stores/rootStore";
import { E2E_IDENTITY_STORAGE_KEY, isE2EAuthBypassEnabled } from "../auth/e2eAuth";

interface IProps {
  store?: RootStore;
  children?: React.ReactNode;
}

const LoadingScreen = ({ text }: { text: string }) => (
  <Preloader className="fixed inset-0 bg-primary" color="white" text={text} />
);

function tryLoadE2EIdentity(): User | null {
  if (!isE2EAuthBypassEnabled()) return null;

  const raw = localStorage.getItem(E2E_IDENTITY_STORAGE_KEY);
  if (!raw) return null;

  try {
    const parsed = JSON.parse(raw) as { access_token?: unknown };

    if (!parsed || typeof parsed.access_token !== "string") {
      return null;
    }

    return User.fromStorageString(raw);
  } catch {
    return null;
  }
}

const AuthProvider = ({ store, children }: IProps) => {
  const { userStore, manufacturerStore } = store!;
  const { user } = userStore;
  const { manufacturer } = manufacturerStore;

  const [isCheckingSession, setIsCheckingSession] = useState(true);

  const hasE2EIdentity = isE2EAuthBypassEnabled() && Boolean(localStorage.getItem(E2E_IDENTITY_STORAGE_KEY));

  useEffect(() => {
    let isMounted = true;

    (async () => {
      try {
        const e2eUser = tryLoadE2EIdentity();
        if (e2eUser) {
          await userStore.storeUser(e2eUser);
        }

        await userStore.getUser();
      } finally {
        if (isMounted) setIsCheckingSession(false);
      }
    })();

    return () => {
      isMounted = false;
    };
  }, [userStore]);

  useEffect(() => {
    if (isCheckingSession) return;
    if (user == null) {
      if (hasE2EIdentity) return;
      userStore.signIn();
    }
  }, [hasE2EIdentity, isCheckingSession, user, userStore]);

  useEffect(() => {
    if (manufacturer === null) {
      window.location.replace(`https://dashboard.randmar.io`);
    }
  }, [manufacturer]);

  if (isCheckingSession) {
    return <LoadingScreen text="Loading your session..." />;
  }

  if (user == null) {
    if (hasE2EIdentity) {
      return <LoadingScreen text="Waiting for e2e identity..." />;
    }
    return <LoadingScreen text="Signing you in..." />;
  }

  if (!manufacturer) return <LoadingScreen text="Loading your profile..." />;

  return <>{children}</>;
};

export default inject("store")(observer(AuthProvider));
